import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { RecurringUsageRule, StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

const ERROR_KEYS = new Set([
  "contract_not_found",
  "permission_denied",
  "invalid_billing_timing",
  "invalid_usage_limit",
  "invalid_price",
  "contract_cancelled",
  "plan_not_found",
  "invalid_future_scope",
]);

function useConfigMutation<TInput, TResult>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  run: (input: TInput) => Promise<{ data: any; error: any }>,
  successKey: string,
): UseMutationResult<TResult, unknown, TInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (input: TInput) => {
      const { data, error } = await run(input);
      if (error) throw error;
      return data as TResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      toast({ title: t(successKey) });
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      const code = ERROR_KEYS.has(message) ? message : "unknown";
      toast({ variant: "destructive", title: t(`stayPlanConfig.errors.${code}`) });
    },
  });
}

export interface UpdateStayPlanTermsInput {
  module: StayPlanModule;
  contractId: string;
  usageLimit?: number | null;
  price?: number | null;
  carryoverEnabled?: boolean | null;
  billingTiming?: "start" | "end" | null;
}

export function useUpdateStayPlanTerms() {
  return useConfigMutation<UpdateStayPlanTermsInput, void>(
    (input) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).rpc("update_stay_plan_contract_terms", {
        p_module: input.module,
        p_contract_id: input.contractId,
        p_usage_limit: input.usageLimit ?? null,
        p_price: input.price ?? null,
        p_carryover_enabled: input.carryoverEnabled ?? null,
        p_billing_timing: input.billingTiming ?? null,
      }),
    "stayPlanConfig.savedTerms",
  );
}

/** O que fazer com as reservas futuras ao pausar ou cancelar um plano. */
export type FutureReservationsScope = "plan" | "all";

export interface SetStayPlanPausedInput {
  module: StayPlanModule;
  contractId: string;
  paused: boolean;
  /** Só vale ao pausar creche. */
  futureScope?: FutureReservationsScope;
}

export function useSetStayPlanPaused() {
  return useConfigMutation<SetStayPlanPausedInput, void>(
    (input) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).rpc("set_stay_plan_contract_paused", {
        p_module: input.module,
        p_contract_id: input.contractId,
        p_paused: input.paused,
        p_future_scope: input.futureScope ?? "plan",
      }),
    "stayPlanConfig.savedPause",
  );
}

export interface SwitchStayPlanInput {
  module: StayPlanModule;
  contractId: string;
  newPlanId: string;
  startsOn?: string | null;
  /** Regra do contrato atual: só `per_week` de creche é matrícula. */
  usageRule?: RecurringUsageRule | null;
  /** Dias fixos do plano novo. Ignorado fora da matrícula. */
  fixedWeekdays?: number[];
}

export interface SwitchProrationResult {
  contract_id: string;
  proration_amount: number;
  proration_mode: string;
}

/**
 * Matrícula de creche tem RPC própria: ela prorrateia o ciclo, grava os dias
 * fixos do plano novo e materializa as diárias. Hotel e planos sem dias fixos
 * continuam na RPC genérica, que não faz acerto nenhum.
 */
export async function switchStayPlan(input: SwitchStayPlanInput): Promise<SwitchProrationResult | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = supabase as any;

  if (input.module === "daycare" && input.usageRule === "per_week") {
    const { data, error } = await client.rpc("switch_daycare_plan_contract", {
      p_contract_id: input.contractId,
      p_new_plan_id: input.newPlanId,
      p_weekdays: input.fixedWeekdays ?? [],
      p_starts_on: input.startsOn ?? null,
    });
    if (error) throw error;
    return (data as SwitchProrationResult) ?? null;
  }

  const { error } = await client.rpc("switch_stay_plan_contract", {
    p_module: input.module,
    p_contract_id: input.contractId,
    p_new_plan_id: input.newPlanId,
    p_starts_on: input.startsOn ?? null,
  });
  if (error) throw error;
  return null;
}

export function useSwitchStayPlan() {
  return useConfigMutation<SwitchStayPlanInput, string>(
    async (input) => {
      const data = await switchStayPlan(input);
      return { data, error: null };
    },
    "stayPlanConfig.savedSwitch",
  );
}
